/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import DataTableNotificationsMainUser from "components/DataTableNotificationsMainUser";
import { Modal } from "@mui/material";
import {
  sendReadNotificationServer,
  sendRemoveNotificationServer,
} from "store/actions/notificationsActions";
import ApartmentNotificationsModal from "./apartmentNotificationsModal";

function CellDate({ value }) {
  return <span>{moment(value).format("DD/MM/YYYY HH:mm")}</span>;
}

const columns = () => {
  const { t } = useTranslation();
  return [
    {
      Header: t("project"),
      accessor: "projectName",
      width: 20,
    },
    {
      Header: t("apartment"),
      accessor: "apartmentName",
      width: 20,
    },
    {
      Header: t("unreadNumber"),
      accessor: "unreadNumber",
      width: 20,
    },
    {
      Header: t("unreadByClientNumber"),
      accessor: "unreadByClientNumber",
      width: 20,
    },
    {
      Header: t("totalNumber"),
      accessor: "totalNumber",
      width: 20,
    },
    {
      Header: t("lastCreatedAt"),
      accessor: "lastCreatedAt",
      Cell: CellDate,
      maxWidth: 100,
    },
  ];
};

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function Notifications({
  notifications,
  isClient,
  actSendReadNotificationServer,
  actSendRemoveNotificationServer,
}) {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedApartmentData, setSelectedApartmentData] = useState({});

  const [actionType, setActionType] = useState("");
  const [formatedData, setFormatedData] = useState([]);

  const { t } = useTranslation();

  const remove = (data) => {
    if (Array.isArray(data)) {
      const notificationsIndexes = [];

      const notificationsData = Object.values(data[0]?.original?.notifications || {});
      if (Array.isArray(notificationsData)) {
        notificationsData.forEach((notification) => {
          const { uid } = notification;
          notificationsIndexes.push(uid);
        });
        setActionType("remove");
        setUids(notificationsIndexes);
        setOpenConfirm(true);
      }
    }
  };

  const confirm = () => {
    switch (actionType) {
      case "remove":
        uids.forEach((id) => {
          if (id && selectedApartmentData?.apartmentId) {
            actSendRemoveNotificationServer(id, selectedApartmentData.apartmentId);
          }
        });
        setActionType("");
        setOpenConfirm(false);
        break;
      default:
        break;
    }
  };

  const read = (data, readState) => {
    if (Array.isArray(data)) {
      if (!readState) return;

      const notificationsData = Object.values(data[0]?.original?.notifications || {});
      if (Array.isArray(notificationsData)) {
        notificationsData.forEach((notification) => {
          const { uid, apartmentId } = notification;
          if (uid && apartmentId) {
            actSendReadNotificationServer(uid, apartmentId);
          }
        });
        setActionType("read");
      }
    }
  };

  const removeFromModal = (uidsArray) => {
    const apartmentId = selectedApartmentData?.apartmentId;
    if (!apartmentId) return;

    uidsArray.forEach((id) => {
      if (id) actSendRemoveNotificationServer(id, apartmentId);
    });
  };
  const editFromModal = (newNotifications) => {
    Object.values(newNotifications || {}).forEach((notification) => {
      const id = notification?.uid;
      const apartmentId = notification?.apartmentId || selectedApartmentData?.apartmentId;
      if (id && apartmentId && notification?.readByMainUser) {
        actSendReadNotificationServer(id, apartmentId);
      }
    });
  };

  useEffect(() => {
    const notificationsArray = Object.values(notifications || {});
    let foundApartment = false;

    const formatedNotifications = notificationsArray.map((apartment) => {
      if (selectedApartmentData?.apartmentId === apartment?.apartmentId) {
        setSelectedApartmentData(apartment);
        foundApartment = true;
      }

      return apartment;
    });
    if (!foundApartment) {
      setOpen(false);
    }
    setFormatedData(formatedNotifications);
  }, [notifications]);

  return (
    <DashboardLayout>
      <AlertDialog
        open={openConfirm}
        handleClose={() => {
          setOpenConfirm(false);
          setSelectedApartmentData({});
        }}
        handleAgree={() => {
          confirm();
        }}
      />
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <ApartmentNotificationsModal
            apartmentData={selectedApartmentData}
            handleRemove={(data) => {
              removeFromModal(data);
            }}
            handleEdit={(data) => {
              editFromModal(data);
            }}
          />
        </MDBox>
      </Modal>
      <DashboardNavbar
        title="notifications"
        route={isClient ? "/client/notifications" : "/notifications"}
        isClient={isClient}
      />
      <MDBox pt={6} pb={3}>
        <Card>
          <CardTitle title={t("notifications")} icon="notifications" />
          <DataTableNotificationsMainUser
            table={{
              columns: columns(),
              rows: formatedData,
            }}
            canSearch
            canSelect
            read={read}
            remove={remove}
            canActuate
            action={(data) => {
              setSelectedApartmentData(data?.original || {});
              setOpen(true);
            }}
            actionIcon="check"
            actionColor="success"
          />
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

Notifications.defaultProps = {
  notifications: {},
  isClient: false,
};

Notifications.propTypes = {
  notifications: PropTypes.object,
  isClient: PropTypes.bool,
  actSendReadNotificationServer: PropTypes.func.isRequired,
  actSendRemoveNotificationServer: PropTypes.func.isRequired,
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};

CellDate.defaultProps = {
  value: 0,
};

CellDate.propTypes = {
  value: PropTypes.any,
};

const mapStateToProps = (state) => {
  const { notificationsReducer, mainUserReducer } = state;

  const notifications = notificationsReducer?.notificationsByApartment || {};
  const { isClient } = mainUserReducer || {};

  return {
    notifications,
    isClient,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actSendReadNotificationServer: (id, apartmentId) => {
    dispatch(sendReadNotificationServer(id, apartmentId));
  },
  actSendRemoveNotificationServer: (id, apartmentId) => {
    dispatch(sendRemoveNotificationServer(id, apartmentId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
